import { AxiosPromise } from 'axios'
import { useAxiosClient } from './configs/axios-setup'

export type FailedTaskFilters = {
  workflowType?: string
  submissionId?: string
  caseId?: string
  taskName?: string
  status?: string
  failedFrom?: number
  failedTo?: number
  submissionFrom?: number
  submissionTo?: number
  runVersion?: string
  includeReferenceRuns?: boolean
  skip?: number
  limit?: number
  sortField?: string
  sortOrder?: 'asc' | 'desc'
}

type HookState = { data: any; loading: boolean; error: unknown }

type FailedTasksHook = [HookState, (filters?: FailedTaskFilters) => AxiosPromise<any>, () => void]

type BulkResubmitHook = [HookState, (tasks: unknown[]) => AxiosPromise<any>, () => void]

const buildQueryString = (
  filters: Record<string, string | number | boolean | undefined> = {}
): string => {
  const params = new URLSearchParams()
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params.append(key, String(value))
    }
  })
  const qs = params.toString()
  return qs ? `?${qs}` : ''
}

export const useGetFailedTasks = (): FailedTasksHook => {
  const [{ data, loading, error }, doFetch] = useAxiosClient(
    {
      url: '/FailedWorkflowTasks',
      method: 'GET'
    },
    { manual: true, autoCancel: false, useCache: false }
  )

  const refetch = (filters?: FailedTaskFilters) => {
    return doFetch({
      url: `/FailedWorkflowTasks${buildQueryString(filters)}`
    })
  }

  return [
    { data, loading, error },
    refetch,
    () => {
      return
    }
  ]
}

export const useBulkResubmitTasks = (): BulkResubmitHook => {
  const [{ data, loading, error }, doFetch] = useAxiosClient(
    {
      url: '/FailedWorkflowTasks/bulk-resubmit',
      method: 'POST'
    },
    { manual: true, autoCancel: false, useCache: false }
  )

  const refetch = (tasks: unknown[]) => {
    return doFetch({
      url: '/FailedWorkflowTasks/bulk-resubmit',
      data: { tasks }
    })
  }

  return [
    { data, loading, error },
    refetch,
    () => {
      return
    }
  ]
}

export type StalledStarccmFilters = {
  submissionId?: string
  caseId?: string
  status?: string
  submissionFrom?: number
  submissionTo?: number
  includeAll?: boolean
  skip?: number
  limit?: number
  sortField?: string
  sortOrder?: 'asc' | 'desc'
}

export type ForceCompleteItem = {
  submissionId: string
  workflowRunGroupSysId?: string
  workflowSystemRunId?: string
  taskId?: string
}

export const useGetStalledStarccmTasks = (): [
  HookState,
  (filters?: StalledStarccmFilters) => AxiosPromise<any>
] => {
  const [{ data, loading, error }, doFetch] = useAxiosClient(
    { url: '/FailedWorkflowTasks/stalled-starccm', method: 'GET' },
    { manual: true, autoCancel: false, useCache: false }
  )

  const refetch = (filters?: StalledStarccmFilters) =>
    doFetch({ url: `/FailedWorkflowTasks/stalled-starccm${buildQueryString(filters)}` })

  return [{ data, loading, error }, refetch]
}

export const useForceCompleteStarccmTasks = (): [
  HookState,
  (tasks: ForceCompleteItem[]) => AxiosPromise<any>
] => {
  const [{ data, loading, error }, doFetch] = useAxiosClient(
    { url: '/FailedWorkflowTasks/force-complete', method: 'POST' },
    { manual: true, autoCancel: false, useCache: false }
  )

  const refetch = (tasks: ForceCompleteItem[]) =>
    doFetch({ url: '/FailedWorkflowTasks/force-complete', data: { tasks } })

  return [{ data, loading, error }, refetch]
}
